import { useEffect } from 'react';

import { VarianceCode } from '__generated__/globalTypes';
import type { AccountVariance } from './getAccountRemittanceVariances_gql';

interface ArrowSwitchOptions {
  enabled: boolean;
  variances: AccountVariance[];
  focusedCode: VarianceCode | null;
  onSwitch: (code: VarianceCode) => void;
}

// Wires window keydown for ←/→ to cycle through `variances` (wrap-around). Disabled
// while the user is typing in a textarea/input so arrow keys still move the caret.
export const useVarianceArrowSwitch = ({
  enabled,
  variances,
  focusedCode,
  onSwitch,
}: ArrowSwitchOptions): void => {
  useEffect(() => {
    if (!enabled || focusedCode === null || variances.length < 2) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;

      const currentIndex = variances.findIndex((v) => v.variance_code === focusedCode);
      if (currentIndex === -1) return;

      const delta = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (currentIndex + delta + variances.length) % variances.length;
      event.preventDefault();
      onSwitch(variances[nextIndex].variance_code);
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, focusedCode, variances, onSwitch]);
};
